/**
 * Utilidad para validación de esquemas JSON usando AJV
 * Proporciona funciones para validar datos DTE contra esquemas predefinidos
 */

import Ajv from 'ajv';
import addFormats from 'ajv-formats';

// Importar esquemas JSON desde la carpeta local
import fcSchema from '../schemas/fe-f-v2.json';
import ccfSchema from '../schemas/fe-ccf-v4.json';
import ncSchema from '../schemas/fe-nc-v4.json';
import ndSchema from '../schemas/fe-nd-v4.json';
import crSchema from '../schemas/fe-cr-v2.json';
import clSchema from '../schemas/fe-cl-v2.json';
import dclSchema from '../schemas/fe-dcl-v2.json';
import fexSchema from '../schemas/fe-fex-v3.json';
import fseSchema from '../schemas/fe-fse-v2.json';
import cdSchema from '../schemas/fe-cd-v2.json';

// Mapeo de tipos de documento a esquemas
const SCHEMAS = {
  '01': { schema: fcSchema, name: 'Factura Electrónica' },
  '03': { schema: ccfSchema, name: 'Comprobante de Crédito Fiscal' },
  '05': { schema: ncSchema, name: 'Nota de Crédito' },
  '06': { schema: ndSchema, name: 'Nota de Débito' },
  '07': { schema: crSchema, name: 'Comprobante de Retención' },
  '08': { schema: clSchema, name: 'Comprobante de Liquidación' },
  '09': { schema: dclSchema, name: 'Documento de Liquidación' },
  '11': { schema: fexSchema, name: 'Factura de Exportación' },
  '14': { schema: fseSchema, name: 'Factura Sujeto Excluido' },
  '15': { schema: cdSchema, name: 'Comprobante de Donación' },
};

// Inicializar AJV con formatos soportados
const ajv = new Ajv({ 
  allErrors: true, 
  verbose: true,
  strict: false,
  validateSchema: false
});
addFormats(ajv);

// Compilar esquemas una sola vez para mejor rendimiento
const compiledSchemas = {};
for (const [tipoDte, { schema, name }] of Object.entries(SCHEMAS)) {
  try {
    compiledSchemas[tipoDte] = {
      validate: ajv.compile(schema),
      schema,
      name
    };
  } catch (error) {
    console.error(`Error compilando esquema ${tipoDte}:`, error);
  }
}

const formatAjvPath = (instancePath = '', params = {}) => {
  const tokens = String(instancePath || '').split('/').filter(Boolean).map((token) => token.replace(/~1/g, '/').replace(/~0/g, '~'));
  let path = '';
  for (const token of tokens) {
    path += /^\d+$/.test(token) ? `[${token}]` : `${path ? '.' : ''}${token}`;
  }
  if (params.missingProperty) path += `${path ? '.' : ''}${params.missingProperty}`;
  return path || 'documento';
};

const getValidationSection = (path) => {
  const root = String(path || '').split(/[.[]/)[0];
  if (root === 'identificacion') return 'identificacion';
  if (root === 'emisor' || root === 'receptor') return 'receptor';
  if (root === 'cuerpoDocumento') return 'detalle';
  if (root === 'resumen') return 'resumen';
  return 'documento';
};

/**
 * Obtiene el esquema compilado para un tipo de documento
 * @param {string} tipoDte - Tipo de documento (01, 03, 05, etc.)
 * @returns {object|null} Esquema compilado o null si no existe
 */
export const getSchema = (tipoDte) => {
  return compiledSchemas[tipoDte] || null;
};

/**
 * Obtiene la lista de campos requeridos para un tipo de documento
 * @param {string} tipoDte - Tipo de documento
 * @param {string} seccion - Sección del documento (identificacion, emisor, receptor, etc.)
 * @returns {string[]} Array de campos requeridos
 */
export const getRequiredFields = (tipoDte, seccion = null) => {
  const schemaData = compiledSchemas[tipoDte];
  if (!schemaData) return [];

  const { schema } = schemaData;
  
  if (seccion) {
    const seccionSchema = seccion.split('.').reduce((current, key) => current?.properties?.[key], schema);
    if (seccionSchema?.required) {
      return seccionSchema.required;
    }
  }
  
  // Retornar campos requeridos del documento raíz
  return schema.required || [];
};

/**
 * Obtiene las reglas de validación para un campo específico
 * @param {string} tipoDte - Tipo de documento
 * @param {string} seccion - Sección del documento
 * @param {string} campo - Nombre del campo
 * @returns {object} Objeto con las reglas de validación
 */
export const getFieldRules = (tipoDte, seccion, campo) => {
  const schemaData = compiledSchemas[tipoDte];
  if (!schemaData) return {};

  const { schema } = schemaData;
  const seccionSchema = schema.properties?.[seccion];
  
  if (!seccionSchema) return {};
  
  const campoSchema = seccionSchema.properties?.[campo];
  
  if (!campoSchema) return {};
  
  return {
    description: campoSchema.description || '',
    type: campoSchema.type,
    minLength: campoSchema.minLength,
    maxLength: campoSchema.maxLength,
    pattern: campoSchema.pattern,
    format: campoSchema.format,
    enum: campoSchema.enum,
    minimum: campoSchema.minimum,
    maximum: campoSchema.maximum,
    exclusiveMinimum: campoSchema.exclusiveMinimum,
    exclusiveMaximum: campoSchema.exclusiveMaximum,
    multipleOf: campoSchema.multipleOf,
    minItems: campoSchema.minItems,
    maxItems: campoSchema.maxItems,
  };
};

/**
 * Valida un objeto de datos contra el esquema correspondiente
 * @param {string} tipoDte - Tipo de documento
 * @param {object} data - Datos a validar
 * @returns {object} Resultado de la validación
 */
export const validateAgainstSchema = (tipoDte, data) => {
  const schemaData = compiledSchemas[tipoDte];
  
  if (!schemaData) {
    return {
      valid: false,
      errors: [{ message: `Tipo de documento ${tipoDte} no soportado` }]
    };
  }

  const { validate, name } = schemaData;
  const valid = validate(data);

  if (valid) {
    return { valid: true, errors: [] };
  }

  // Transformar errores de AJV a formato más legible
  const errors = (validate.errors || []).map(error => ({
    path: error.instancePath || '/',
    field: error.params?.missingProperty || error.instancePath?.replace(/\//g, '.').substring(1) || error.params?.allowedValues?.[0],
    message: formatErrorMessage(error),
    keyword: error.keyword,
    params: error.params
  }));

  return { valid: false, errors, documentType: name };
};

/**
 * Formatea el mensaje de error de AJV a un formato más legible
 * @param {object} error - Error de AJV
 * @returns {string} Mensaje formateado
 */
const formatErrorMessage = (error) => {
  const { keyword, message, params, instancePath } = error;
  const fieldName = instancePath?.replace(/\//g, '.').substring(1) || 'campo';

  switch (keyword) {
    case 'required':
      return `El campo "${params.missingProperty}" es requerido`;
    
    case 'type':
      return `El campo "${fieldName}" debe ser de tipo ${params.type}`;
    
    case 'minLength':
      return `El campo "${fieldName}" debe tener al menos ${params.limit} caracteres`;
    
    case 'maxLength':
      return `El campo "${fieldName}" debe tener como máximo ${params.limit} caracteres`;
    
    case 'pattern':
      return `El campo "${fieldName}" no tiene el formato correcto`;
    
    case 'enum':
      return `El campo "${fieldName}" debe ser uno de: ${params.allowedValues?.join(', ')}`;
    
    case 'minimum':
      return `El campo "${fieldName}" debe ser mayor o igual a ${params.limit}`;
    
    case 'maximum':
      return `El campo "${fieldName}" debe ser menor o igual a ${params.limit}`;
    
    case 'format':
      return `El campo "${fieldName}" debe tener formato válido (${params.format})`;

    case 'minItems':
      return `El campo "${fieldName}" debe contener al menos ${params.limit} elemento(s)`;

    case 'maxItems':
      return `El campo "${fieldName}" debe contener como máximo ${params.limit} elemento(s)`;

    case 'multipleOf':
      return `El campo "${fieldName}" debe ser múltiplo de ${params.multipleOf}`;

    case 'const':
      return `El campo "${fieldName}" debe tener el valor ${params.allowedValue}`;
    
    case 'additionalProperties':
      return `El campo "${params.additionalProperty}" no es válido para este documento`;
    
    default:
      return message || `Error en el campo "${fieldName}"`;
  }
};

/**
 * Valida el payload final que será enviado al backend/MH.
 * Es la entrada recomendada para los wizards porque conserva los paths
 * reales del contrato final, incluyendo índices de arrays.
 */
export const validateDtePayload = (data) => {
  const tipoDte = String(data?.identificacion?.tipoDte || '').trim();
  const result = validateAgainstSchema(tipoDte, data);
  if (result.valid) return { ...result, diagnostics: [] };

  const diagnostics = (result.errors || []).map((error) => {
    const path = formatAjvPath(error.path, error.params);
    return {
      ...error,
      path,
      section: getValidationSection(path),
      message: formatErrorMessage({
        keyword: error.keyword,
        message: error.message,
        params: error.params || {},
        instancePath: error.path,
      }),
    };
  });

  return { ...result, diagnostics };
};

/**
 * Valida una sección específica del documento
 * @param {string} tipoDte - Tipo de documento
 * @param {string} seccion - Sección a validar (identificacion, emisor, receptor, etc.)
 * @param {object} data - Datos de la sección
 * @returns {object} Resultado de la validación
 */
export const validateSection = (tipoDte, seccion, data) => {
  const schemaData = compiledSchemas[tipoDte];
  
  if (!schemaData) {
    return {
      valid: false,
      errors: [{ message: `Tipo de documento ${tipoDte} no soportado` }]
    };
  }

  const { schema } = schemaData;
  const seccionSchema = schema.properties?.[seccion];
  
  if (!seccionSchema) {
    return {
      valid: false,
      errors: [{ message: `Sección "${seccion}" no encontrada en el esquema` }]
    };
  }

  // Crear validador específico para la sección
  const sectionValidator = ajv.compile({
    type: 'object',
    properties: seccionSchema.properties,
    required: seccionSchema.required || [],
    additionalProperties: seccionSchema.additionalProperties ?? false,
    allOf: seccionSchema.allOf
  });

  const valid = sectionValidator(data);
  
  if (valid) {
    return { valid: true, errors: [] };
  }

  const errors = (sectionValidator.errors || []).map(error => ({
    path: `${seccion}.${error.instancePath?.replace(/\//g, '.').substring(1) || ''}`,
    field: error.params?.missingProperty || error.instancePath?.replace(/\//g, '.').substring(1),
    message: formatErrorMessage(error),
    keyword: error.keyword
  }));

  return { valid: false, errors };
};

/**
 * Obtiene todos los tipos de documento disponibles
 * @returns {array} Lista de tipos de documento
 */
export const getDocumentTypes = () => {
  return Object.entries(compiledSchemas).map(([tipoDte, { name }]) => ({
    code: tipoDte,
    name
  }));
};

/**
 * Obtiene la descripción de un tipo de documento
 * @param {string} tipoDte - Tipo de documento
 * @returns {string} Nombre del tipo de documento
 */
export const getDocumentTypeName = (tipoDte) => {
  return compiledSchemas[tipoDte]?.name || 'Desconocido';
};

export default {
  getSchema,
  getRequiredFields,
  getFieldRules,
  validateAgainstSchema,
  validateDtePayload,
  validateSection,
  getDocumentTypes,
  getDocumentTypeName
};
